import React, { forwardRef } from 'react';

interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  label?: string | React.ReactNode;
  error?: string;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'rounded';
  fullWidth?: boolean;
  indeterminate?: boolean;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      error,
      helperText,
      size = 'md',
      variant = 'default',
      fullWidth = false,
      indeterminate = false,
      className = '',
      style = {},
      checked,
      onChange,
      disabled,
      ...props
    },
    ref
  ) => {
    const getSizeClasses = (): { checkbox: string; label: string } => {
      switch (size) {
        case 'sm':
          return {
            checkbox: 'w-4 h-4',
            label: 'text-xs',
          };
        case 'lg':
          return {
            checkbox: 'w-6 h-6',
            label: 'text-base',
          };
        default:
          return {
            checkbox: 'w-5 h-5',
            label: 'text-sm',
          };
      }
    };

    const getBorderRadius = (): string => {
      return variant === 'rounded' ? 'rounded-full' : 'rounded';
    };

    const checkboxId =
      props.id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;

    const sizeClasses = getSizeClasses();
    const isChecked = checked || indeterminate;

    const checkboxStyles: React.CSSProperties = {
      borderColor: isChecked
        ? 'var(--accent)'
        : error
          ? '#ef4444'
          : 'var(--border)',
      backgroundColor: isChecked
        ? 'var(--accent)'
        : disabled
          ? 'var(--bg-secondary)'
          : 'var(--bg-primary)',
      boxShadow: isChecked ? '0 0 0 2px rgba(var(--accent-rgb), 0.2)' : 'none',
      opacity: disabled ? 0.6 : 1,
      ...style,
    };

    const wrapperClasses = [
      'flex flex-col',
      fullWidth ? 'w-full' : '',
      disabled ? 'cursor-not-allowed' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const checkboxWrapperClasses = [
      'flex items-start space-x-3 group',
      disabled ? 'cursor-not-allowed' : 'cursor-pointer',
    ]
      .filter(Boolean)
      .join(' ');

    const checkboxClasses = [
      sizeClasses.checkbox,
      getBorderRadius(),
      'border-2 cursor-pointer transition-all duration-200 flex items-center justify-center flex-shrink-0 mt-0.5',
      disabled ? 'cursor-not-allowed' : 'group-hover:scale-105',
      'focus:outline-none focus:ring-2 focus:ring-accent focus:ring-opacity-50 focus:ring-offset-2',
    ]
      .filter(Boolean)
      .join(' ');

    const labelClasses = [
      sizeClasses.label,
      'leading-relaxed cursor-pointer transition-colors flex-1 select-none',
      disabled ? 'cursor-not-allowed opacity-60' : '',
    ]
      .filter(Boolean)
      .join(' ');

    const handleCheckboxClick = () => {
      if (disabled) return;

      const syntheticEvent = {
        target: { checked: !checked },
        currentTarget: { checked: !checked },
      } as React.ChangeEvent<HTMLInputElement>;

      onChange?.(syntheticEvent);
    };

    const renderCheckIcon = () => {
      if (indeterminate) {
        return (
          <svg
            className={`${size === 'sm' ? 'w-2 h-2' : size === 'lg' ? 'w-4 h-4' : 'w-3 h-3'} text-white animate-[fadeIn_0.2s_ease-in-out]`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <rect x="4" y="9" width="12" height="2" rx="1" />
          </svg>
        );
      }

      if (checked) {
        return (
          <svg
            className={`${size === 'sm' ? 'w-2 h-2' : size === 'lg' ? 'w-4 h-4' : 'w-3 h-3'} text-white animate-[fadeIn_0.2s_ease-in-out]`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        );
      }

      return null;
    };

    return (
      <div className={wrapperClasses}>
        <div className={checkboxWrapperClasses}>
          <div className="relative flex-shrink-0">
            <input
              ref={ref}
              type="checkbox"
              id={checkboxId}
              checked={checked}
              onChange={onChange}
              disabled={disabled}
              className="peer sr-only"
              {...props}
            />
            <div
              className={checkboxClasses}
              style={checkboxStyles}
              onClick={handleCheckboxClick}
            >
              {renderCheckIcon()}
            </div>
          </div>

          {label && (
            <label
              htmlFor={checkboxId}
              className={labelClasses}
              style={{
                color: isChecked
                  ? 'var(--text-primary)'
                  : error
                    ? '#ef4444'
                    : 'var(--text-secondary)',
              }}
              onClick={handleCheckboxClick}
            >
              {label}
            </label>
          )}
        </div>

        {(error || helperText) && (
          <div className={`mt-1 text-xs ${label ? 'ml-8' : ''}`}>
            {error ? (
              <span className="text-red-500">{error}</span>
            ) : (
              <span style={{ color: 'var(--text-tertiary)' }}>
                {helperText}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
